import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

const ContactPage = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const faqItems = t("faq.items", { returnObjects: true }) as { q: string; a: string }[];

  const contactChannels = [
    {
      icon: "ri-whatsapp-line",
      color: "bg-[#25D366]",
      textColor: "text-white",
      title: lang === "en" ? "Chat on WhatsApp" : "Escríbenos por WhatsApp",
      subtitle: lang === "en"
        ? "The fastest way to reach us. Send a message and we'll reply in minutes."
        : "La forma más rápida de contactarnos. Escríbenos y respondemos en minutos.",
      cta: lang === "en" ? "Open WhatsApp" : "Abrir WhatsApp",
      href: "https://wa.me/15612626118?text=Hi!%20I%27d%20like%20to%20request%20a%20quote%20for%20your%20services.",
      badge: lang === "en" ? "Fastest Response" : "Respuesta más rápida",
    },
    {
      icon: "ri-phone-line",
      color: "bg-brand-dark",
      textColor: "text-white",
      title: lang === "en" ? "Call Us Directly" : "Llámanos directamente",
      subtitle: lang === "en"
        ? "Prefer to talk? Give us a call and we'll help you right away."
        : "¿Prefieres hablar? Llámanos y te atendemos de inmediato.",
      cta: "(561) 262-6118",
      href: "tel:+15612626118",
      badge: null,
    },
    {
      icon: "ri-mail-send-line",
      color: "bg-brand-beige",
      textColor: "text-brand-dark",
      title: lang === "en" ? "Send an Email" : "Envíanos un correo",
      subtitle: lang === "en"
        ? "For detailed inquiries or documentation, reach us by email."
        : "Para consultas detalladas o documentación, escríbenos al correo.",
      cta: "adrimich7778@hotmail.com",
      href: "mailto:adrimich7778@hotmail.com",
      badge: null,
    },
  ];

  const quickMessages = lang === "en"
    ? [
        "I'd like a free quote",
        "I need a deep cleaning",
        "Tell me about your services",
        "I need window cleaning",
        "Airbnb / short-term rental cleaning",
        "Move-in / Move-out cleaning",
      ]
    : [
        "Quiero un presupuesto gratis",
        "Necesito una limpieza profunda",
        "Cuéntame sobre sus servicios",
        "Necesito limpieza de ventanas",
        "Limpieza Airbnb / alquiler corto",
        "Limpieza de mudanza",
      ];

  return (
    <>
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative pt-32 pb-16 bg-brand-dark overflow-hidden">
          <div className="absolute inset-0 opacity-20">
            <img
              src="https://readdy.ai/api/search-image?query=contact%20us%20customer%20service%20professional%20communication%20phone%20call%20friendly%20team%20Florida%20home%20service&width=1920&height=400&seq=cont1&orientation=landscape"
              alt=""
              className="w-full h-full object-cover object-top"
            />
          </div>
          <div className="relative max-w-7xl mx-auto px-4 md:px-6 text-center">
            <div className="flex items-center justify-center gap-2 text-white/50 text-sm font-body mb-4">
              <Link to="/" className="hover:text-white transition-colors cursor-pointer">Home</Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-white">{lang === "en" ? "Contact" : "Contacto"}</span>
            </div>
            <h1 className="font-heading font-black text-white text-4xl md:text-6xl mb-4">
              {t("hero.title")}
            </h1>
            <p className="font-body text-white/60 text-base md:text-lg mb-8">
              {t("hero.subtitle")}
            </p>
          </div>
        </section>

        {/* Contact Channels */}
        <section className="py-20 bg-white">
          <div className="max-w-5xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="font-heading font-bold text-3xl md:text-4xl text-brand-dark mb-3">
                {lang === "en" ? "Get in Touch" : "Contáctanos"}
              </h2>
              <p className="font-body text-gray-500 text-base max-w-xl mx-auto">
                {lang === "en"
                  ? "Choose the channel that works best for you. We're available 7 days a week."
                  : "Elige el canal que mejor te funcione. Estamos disponibles los 7 días de la semana."}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
              {contactChannels.map((ch, i) => (
                <a
                  key={i}
                  href={ch.href}
                  target={ch.href.startsWith("http") ? "_blank" : undefined}
                  rel="noopener noreferrer"
                  className={`relative flex flex-col items-center text-center rounded-2xl p-8 transition-all hover:scale-105 cursor-pointer ${ch.color} ${ch.textColor}`}
                >
                  {ch.badge && (
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-brand-green text-white text-xs font-heading font-bold px-3 py-1 rounded-full whitespace-nowrap">
                      {ch.badge}
                    </span>
                  )}
                  <div className="w-16 h-16 flex items-center justify-center rounded-full bg-white/20 mb-5">
                    <i className={`${ch.icon} text-3xl`}></i>
                  </div>
                  <h3 className="font-heading font-bold text-lg mb-2">{ch.title}</h3>
                  <p className={`font-body text-sm mb-5 leading-relaxed ${ch.textColor === "text-white" ? "opacity-80" : "text-gray-500"}`}>
                    {ch.subtitle}
                  </p>
                  <span className={`font-heading font-semibold text-sm px-5 py-2.5 rounded-full whitespace-nowrap ${ch.textColor === "text-white" ? "bg-white/20 hover:bg-white/30" : "bg-brand-dark text-white"} transition-colors`}>
                    {ch.cta}
                  </span>
                </a>
              ))}
            </div>

            {/* Quick WhatsApp Messages */}
            <div className="bg-brand-beige rounded-2xl p-8 md:p-10">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 flex items-center justify-center bg-[#25D366] rounded-xl">
                  <i className="ri-whatsapp-line text-white text-xl"></i>
                </div>
                <div>
                  <h3 className="font-heading font-bold text-brand-dark text-lg">
                    {lang === "en" ? "Quick Message Templates" : "Mensajes rápidos para WhatsApp"}
                  </h3>
                  <p className="font-body text-gray-500 text-sm">
                    {lang === "en" ? "Tap any option to open WhatsApp with a pre-written message" : "Toca cualquier opción para abrir WhatsApp con un mensaje listo"}
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap gap-3">
                {quickMessages.map((msg, i) => (
                  <a
                    key={i}
                    href={`https://wa.me/15612626118?text=${encodeURIComponent(msg)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 bg-white border border-gray-200 hover:border-[#25D366] hover:bg-[#25D366]/5 text-brand-dark text-sm font-body px-4 py-2.5 rounded-full transition-all cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-send-plane-line text-[#25D366] text-xs"></i>
                    {msg}
                  </a>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Info Cards */}
        <section className="py-12 bg-brand-beige/40">
          <div className="max-w-5xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { icon: "ri-map-pin-line", label: t("info.address.label"), value: t("info.address.value"), href: "https://maps.google.com/?q=10639+Misty+Lane+Royal+Palm+Beach+FL+33411" },
                { icon: "ri-time-line", label: t("info.schedule.label"), value: t("info.schedule.value"), href: null },
                { icon: "ri-bank-card-line", label: t("info.payment.label"), value: t("info.payment.value"), href: null },
                { icon: "ri-map-2-line", label: lang === "en" ? "Service Area" : "Área de Servicio", value: "Palm Beach County, FL", href: null },
              ].map((item, i) => (
                <div key={i} className="bg-white rounded-2xl p-5 flex items-start gap-3">
                  <div className="w-9 h-9 flex items-center justify-center bg-brand-green/10 rounded-xl flex-shrink-0">
                    <i className={`${item.icon} text-brand-green`}></i>
                  </div>
                  <div>
                    <p className="font-heading font-semibold text-brand-dark text-xs mb-0.5">{item.label}</p>
                    {item.href ? (
                      <a href={item.href} target="_blank" rel="noopener noreferrer" className="font-body text-gray-500 text-xs hover:text-brand-green transition-colors cursor-pointer leading-snug">{item.value}</a>
                    ) : (
                      <p className="font-body text-gray-500 text-xs leading-snug">{item.value}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Map */}
        <section className="w-full" style={{ height: "450px" }}>
          <iframe
            title="Adry & Mich Location"
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3564.6433440494666!2d-80.21460642477173!3d26.691886876779407!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x88d92f1fb7365f2d%3A0x12c37bf3f18362b2!2s10639%20Misty%20Ln%2C%20Royal%20Palm%20Beach%2C%20FL%2033411%2C%20USA!5e0!3m2!1sen!2sco!4v1775489619297!5m2!1sen!2sco"
            width="100%"
            height="100%"
            style={{ border: 0, display: "block" }}
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </section>

        {/* FAQ */}
        <section className="py-16 bg-white">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <h2 className="font-heading font-bold text-2xl md:text-3xl text-brand-dark text-center mb-8">{t("faq.title")}</h2>
            <div className="flex flex-col gap-3">
              {faqItems.map((item, i) => (
                <div key={i} className="border border-gray-100 rounded-xl overflow-hidden">
                  <button
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer hover:bg-brand-beige transition-colors"
                  >
                    <span className="font-heading font-semibold text-brand-dark text-sm pr-4">{item.q}</span>
                    <i className={`ri-${openFaq === i ? "subtract" : "add"}-line text-brand-green flex-shrink-0`}></i>
                  </button>
                  {openFaq === i && (
                    <div className="px-5 pb-4">
                      <p className="font-body text-gray-600 text-sm leading-relaxed">{item.a}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
};

export default ContactPage;
